import React from 'react'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { ReactComponent as Loader2 } from '../../assets/svgs/loader2.svg'
import { useTheme } from '../../contexts/themeContext'

export const createColors = (theme) => ({
  primary: {
    main: theme.brandColorOrange,
    hover: theme.electricBlue,
    text: theme.baseWhite,
    disabled: {
      background: theme.brandColorOrange,
      text: theme.baseWhite,
    },
  },
  success: {
    main: theme.salemGreen,
    hover: '#0B6b43',
    text: theme.baseWhite,
    disabled: {
      background: theme.salemGreen,
      text: theme.baseWhite,
    },
  },
  error: {
    main: theme.darkRed,
    hover: '#922c2a',
    text: theme.baseWhite,
    disabled: {
      background: theme.darkRed,
      text: theme.baseWhite,
    },
  },
  neutral: {
    main: '#9F9C9C',
    hover: '#7A7878',
    text: theme.neutrals,
    disabled: {
      background: '#9F9C9C',
      text: theme.neutrals,
    },
  },
  secondary: {
    main: '#E3E3E3',
    hover: '#D3D3D3',
    text: theme.brandColorOrange,
    disabled: {
      background: '#E3E3E3',
      text: theme.brandColorOrange,
    },
  },
  surface: {
    light: '#F8F8FB',
    dark: '#433E3F',
    white: theme.baseWhite,
  },
  utility: {
    lightBlue: '#bbccd8',
    black: '#000000',
    darkGray: '#4A4A4A',
  },
  warning: {
    main: theme.warning || '#ffc302',
    hover: '#e6ae00',
    text: theme.warningText || '#725608',
    disabled: {
      background: theme.warning || '#ffc302',
      text: theme.warningText || '#725608',
    },
  },
  extra: {
    main: theme.tertiary,
    hover: '#D3D3D3',
    text: theme.neutrals,
    disabled: {
      background: '#E3E3E3',
      text: theme.brandColorOrange,
    },
  },
})

const sizeMap = {
  xs: '80px',
  sm: '120px',
  base: '160px',
  md: '200px',
  lg: '280px',
  full: '100%',
}

const paddingMap = {
  xs: '0 8px',
  sm: '0 12px',
  base: '0 16px',
  md: '0 16px',
  lg: '0 16px',
  full: '0 16px',
}

const variantStyles = {
  solid: (color) => ({
    backgroundColor: color.main,
    color: color.text,
    '&:hover:not(:disabled)': {
      backgroundColor: color.hover,
      color: color.text,
    },
    '&:disabled': {
      backgroundColor: color.disabled.background,
      color: color.disabled.text,
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  }),
  outlined: (color) => ({
    backgroundColor: 'transparent',
    border: `1px solid ${color.main}`,
    color: color.main,
    '&:hover:not(:disabled)': {
      backgroundColor: color.main,
      color: color.text,
    },
    '&:disabled': {
      borderColor: color.disabled.background,
      color: color.disabled.background,
      cursor: 'not-allowed',
      backgroundColor: 'transparent',
    },
  }),
  ghost: (color) => ({
    backgroundColor: 'transparent',
    color: color.main,
    '&:hover:not(:disabled)': {
      backgroundColor:
        color.hover === '#F8F8FB' ? '#F8F8FB' : 'rgba(74, 74, 74, 0.1)',
      color: color.hover,
    },
    '&:disabled': {
      color: color.disabled?.background || '#9F9C9C',
      cursor: 'not-allowed',
    },
  }),
  link: (color) => ({
    backgroundColor: 'transparent',
    color: color.main,
    textDecoration: 'underline',
    '&:hover:not(:disabled)': {
      textDecoration: 'none',
      color: color.hover,
      backgroundColor:
        color.hover === '#9F9C9C' ? 'transparent' : 'rgba(74, 74, 74, 0.05)',
    },
    '&:disabled': {
      color: color.disabled?.background || '#9F9C9C',
      cursor: 'not-allowed',
    },
  }),
 auth: () => ({
    background: '#34c38f 0% 0% no-repeat padding-box', 
    width: '100%',
    borderRadius: '4px',
    color: '#fff',
    border: 'none',
    padding: '9px',
    marginTop: '12px',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s ease-out',
    
    '&:hover': {
      background: '#70d5b0', 
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  }),
}

const getVariantStyle = (variant, colors) => {
  const [type, colorName] = variant.split('-')
  let colorConfig

  if (!colorName && (type === 'ghost' || type === 'link')) {
    colorConfig = {
      main: colors.utility.darkGray,
      hover: colors.utility.darkGray,
      disabled: {
        background: colors.neutral.main,
        text: colors.neutral.main,
      },
    }
  } else {
    colorConfig = colors[colorName || 'primary']
  }

  return variantStyles[type]?.(colorConfig) || {}
}

const StyledButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'iconOnly',
})(({ variant, size, iconOnly, colors }) => ({
  height: '36px',
  borderRadius: '2px !important',
  padding: variant.startsWith('link')
    ? '0'
    : iconOnly
      ? '0 10px'
      : paddingMap[size] || paddingMap.base,
  width: variant.startsWith('link')
    ? 'auto'
    : iconOnly
      ? '36px'
      : sizeMap[size],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  fontSize: '12px !important',
  fontWeight: 500,
  transition: 'all 0.2s ease-in-out',
  border: 'none',
  cursor: 'pointer',
  '&:focus': { outline: 'none' },
  '& svg': { width: '18px', height: '18px' },
  ...getVariantStyle(variant, colors),
}))

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const AnimatedLoader = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`

export const CoreButton = React.forwardRef(
  (
    {
      variant = 'solid-primary',
      size = 'base',
      isLoading = false,
      disabled = false,
      icon,
      iconOnly = false,
      onClick,
      type = 'button',
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    const { theme } = useTheme()
    const colors = createColors(theme)

    return (
      <StyledButton
        ref={ref}
        variant={variant}
        size={size}
        iconOnly={iconOnly}
        disabled={disabled || isLoading}
        onClick={onClick}
        type={type}
        className={className}
        colors={colors}
        {...rest}
      >
        {isLoading ? (
          <AnimatedLoader />
        ) : (
          <>
            {icon}
            {!iconOnly && children}
          </>
        )}
      </StyledButton>
    )
  },
)

CoreButton.displayName = 'CoreButton'
